/** @typedef {import('../index.js').BaseDocData} BaseDocData */
import * as Yup from 'yup';

export const quoteSchema = Yup.object().shape({
  part: Yup.string().required('Part ID is required'),
  needsReview: Yup.boolean().default(true).required('needsReview is required'),
  hasBeenReviewed: Yup.boolean().default(false).required('hasBeenReviewed is required'),
  status: Yup.string().oneOf(['pending', 'quoted', 'no_quote']).default('pending').required('Status is required'),
  isBudgetary: Yup.boolean().default(false).nullable(),
  pricingVersion: Yup.string().oneOf(['v1', 'v2', 'v3']).default('v3').required('Pricing version is required'),
  feedback: Yup.array().of(
    Yup.object().shape({
      text: Yup.string().required('Feedback text is required'),
      title: Yup.string().required('Feedback title is required'),
      category: Yup.string().nullable(),
      checkboxText: Yup.string().nullable(),
      image: Yup.string().nullable(),
    }),
  ).default([]).required('Feedback is required'),
  optionalFeedback: Yup.array().of(Yup.mixed()).default([]).required('Optional feedback is required'),
  feedbackChecklistVersion: Yup.number().required('feedbackChecklistVersion is required'),
  feedbackChecklistReviewed: Yup.boolean().default(false).nullable(),
  feedbackChecklistPasses: Yup.object().nullable(),
  feedbackResponses: Yup.object().default({}),
  // Canonical DFM selection strings (see getDfmSelectionsString; DfmSelectionsNone
  // for the no-selections configuration) of configurations that have been ordered —
  // a re-order matching one gets the reorder setup fee, any other configuration
  // pays the original setup fee
  orderedDfmSelections: Yup.array().of(Yup.string()).default([]),
  pricingV2: Yup.object().shape({
    setupPrice: Yup.object().shape({
      uncapped: Yup.number().required('PricingV2 setupPrice uncapped is required'),
      capped: Yup.number().required('PricingV2 setupPrice capped is required'),
    }).required('PricingV2 setupPrice is required'),
    pricingLevels: Yup.array().of(
      Yup.object().shape({
        quantity: Yup.number().required('PricingV2 pricingLevel quantity is required'),
        price: Yup.object().shape({
          uncapped: Yup.number().required('PricingV2 pricingLevel price uncapped is required'),
          capped: Yup.number().required('PricingV2 pricingLevel price capped is required'),
        }).required('PricingV2 pricingLevel price is required'),
      }),
    ).required('PricingV2 pricingLevels are required'),
  }).nullable().default(undefined),
  pricingV3: Yup.object().when('pricingVersion', {
    is: 'v3',
    then: Yup.object().shape({
      attributesVersion: Yup.string().required('PricingV3 attributesVersion is required'),
      values: Yup.object().shape({
        attributes: Yup.object().required('PricingV3 values attributes are required'),
        breakpoints: Yup.array().of(
          Yup.object().shape({
            quantity: Yup.number().required('PricingV3 breakpoint quantity is required'),
            multiplier: Yup.number().required('PricingV3 breakpoint multiplier is required'),
          }),
        ).required('PricingV3 values breakpoints are required'),
        calculatedValues: Yup.object().shape({
          cappedSetupPrice: Yup.number().required('PricingV3 calculatedValues cappedSetupPrice is required'),
          uncappedSetupPrice: Yup.number().required('PricingV3 calculatedValues uncappedSetupPrice is required'),
          cappedUnitPrice: Yup.number(),
          uncappedUnitPrice: Yup.number(),
        }).required('PricingV3 values calculatedValues are required'),
        leadtimes: Yup.array().of(
          Yup.object().shape({
            id: Yup.string().required('PricingV3 leadtime id is required'),
            minQuantity: Yup.number().required('PricingV3 leadtime minQuantity is required'),
            maxQuantity: Yup.number().required('PricingV3 leadtime maxQuantity is required'),
            multiplier: Yup.number().required('PricingV3 leadtime multiplier is required'),
            leadtimeText: Yup.string().nullable(),
            leadtimeDays: Yup.number().nullable(),
          }),
        ).required('PricingV3 values leadtimes are required'),
        materials: Yup.array().of(
          Yup.object().shape({
            id: Yup.string().required('materials id is required'),
            display: Yup.string().required('materials display is required'),
            isDefault: Yup.boolean(),
            cappingStyle: Yup.array().of(
              Yup.object().shape({
                id: Yup.string().required('cappingStyle id is required'),
                display: Yup.string().required('cappingStyle display is required'),
                isDefault: Yup.boolean(),
              }).nullable(),
            ),
          }),
        ).required('PricingV3 values materials are required'),
        notes: Yup.string().nullable(),
        reorderSetup: Yup.number().default(500).required('PricingV3 values reorderSetup is required'),
      }).nullable().default(undefined),
    }).required('PricingV3 is required'),
    otherwise: Yup.object().nullable().default(undefined),
  }),
});

/**
 * @typedef {Object} PricingV3Value
 * @property {{ [key: string]: any }} attributes
 * @property {{ quantity: number; multiplier: number }[]} breakpoints
 * @property {{
 *    cappedSetupPrice: number;
 *    uncappedSetupPrice: number;
 *    cappedUnitPrice?: number;
 *    uncappedUnitPrice?: number;
 * }} calculatedValues
 * @property {{
 *    id: string;
 *    minQuantity: number;
 *    maxQuantity?: number;
 *    multiplier: number;
 *    leadtimeText?: string | null;
 *    leadtimeDays?: number | null
 * }[]} leadtimes
 * @property {{
 *    id: string;
 *    display: string;
 *    isDefault?: boolean;
 *    cappingStyle?: ({
 *      id: string;
 *      display: string;
 *      isDefault?: boolean;
 *    } | null)[];
 * }[]} materials
 * @property {string | null | undefined} notes
 * @property {number} reorderSetup
 */

/**
 * @typedef {Object} Feedback
 * @property {string} text
 * @property {string} title
 * @property {string | null | undefined} category
 * @property {string | null | undefined} checkboxText
 * @property {string | null | undefined} image
 */

/**
 * @typedef {Object} PricingV3
 * @property {string} attributesVersion
 * @property {PricingV3Value | null} values
 */

/**
 * @typedef {Object} CustomMaterial
 * @property {string} id
 * @property {string} label
 */

/**
 * @typedef {Object} QuoteSchemaOverrides
 * @property {'pending' | 'quoted' | 'no_quote'} status
 * @property {Feedback[]} feedback
 * @property {any[]} optionalFeedback
 * @property {{ [key: string]: any }} feedbackResponses
 * @property {PricingV3 | null | undefined} pricingV3
 */

/**
 * @typedef {Yup.InferType<typeof quoteSchema>} QuoteSchema
 * @typedef {BaseDocData & Omit<QuoteSchema,
 *            'status' |
 *            'feedback' |
 *            'optionalFeedback' |
 *            'feedbackResponses' |
 *            'pricingV3'>
 *            & QuoteSchemaOverrides} QuoteDocData
 */

/**
 * Class representing a Quote.
 * @class
 */
export default class Quote {
  /** @type {QuoteDocData['validation']} */ validation = 'passed';
  /** Firestore document ID @type {QuoteDocData['id']} */ id;

  /** Core linkage & flags */
  /** @type {QuoteDocData['part']} */ part;
  /** @type {QuoteDocData['isBudgetary']} */ isBudgetary;
  /** @type {QuoteDocData['needsReview']} */ needsReview;
  /** @type {QuoteDocData['status']} */ status;
  /** @type {QuoteDocData['pricingVersion']} */ pricingVersion;

  /** Checklist */
  /** @type {QuoteDocData['feedbackChecklistVersion']} */ feedbackChecklistVersion;
  /** @type {QuoteDocData['feedbackChecklistReviewed']} */ feedbackChecklistReviewed;
  /** @type {QuoteDocData['feedbackChecklistPasses']} */ feedbackChecklistPasses;

  /** Feedback */
  /** @type {QuoteDocData['feedback']} */ feedback;
  /** @type {QuoteDocData['optionalFeedback']} */ optionalFeedback;
  /** @type {QuoteDocData['hasBeenReviewed']} */ hasBeenReviewed;
  /** @type {QuoteDocData['feedbackResponses']} */ feedbackResponses;
  /** @type {QuoteDocData['orderedDfmSelections']} */ orderedDfmSelections;

  /** Pricing payloads */
  /** @type {QuoteDocData['pricingV2']} */ pricingV2;
  /** @type {QuoteDocData['pricingV3']} */ pricingV3;

  /**
   * Create a {@link Quote}.
   * @param {QuoteDocData} param0 - A {@link QuoteDocData} object.
   */
  constructor({
    id,
    part,
    isBudgetary,
    hasBeenReviewed,
    feedback,
    optionalFeedback,
    feedbackChecklistVersion,
    feedbackChecklistPasses,
    feedbackChecklistReviewed,
    feedbackResponses,
    orderedDfmSelections,
    needsReview,
    pricingV2,
    pricingV3,
    pricingVersion,
    status,
  }) {
    const validatedData = quoteSchema.validateSync({
      part,
      isBudgetary,
      hasBeenReviewed,
      feedback,
      optionalFeedback,
      feedbackChecklistVersion,
      feedbackChecklistPasses,
      feedbackChecklistReviewed,
      feedbackResponses,
      orderedDfmSelections,
      needsReview,
      pricingV2,
      pricingV3,
      pricingVersion,
      status,
    });

    this.id = id || null;

    this.part = validatedData.part;
    this.isBudgetary = validatedData.isBudgetary;
    this.hasBeenReviewed = validatedData.hasBeenReviewed;

    this.feedback = validatedData.feedback;
    this.optionalFeedback = validatedData.optionalFeedback;

    this.feedbackChecklistVersion = validatedData.feedbackChecklistVersion;
    this.feedbackChecklistPasses = validatedData.feedbackChecklistPasses || {};
    this.feedbackChecklistReviewed = validatedData.feedbackChecklistReviewed || false;
    this.feedbackResponses = validatedData.feedbackResponses || {};
    this.orderedDfmSelections = validatedData.orderedDfmSelections || [];

    this.needsReview = validatedData.needsReview;

    this.pricingV2 = validatedData.pricingV2;
    this.pricingV3 = validatedData.pricingV3;
    this.pricingVersion = validatedData.pricingVersion;

    this.status = validatedData.status;
  }

  /**
   * Converts the {@link Quote} instance to a Firestore data object.
   * @returns {Omit<QuoteDocData, 'id'>} {@link QuoteDocData} (without `id`).
   */
  toFirestore() {
    // We only allow pricingVersion 'v3' for writing to Firestore
    if (this.pricingVersion !== 'v3') {
      throw new Error(`Unsupported pricing version: ${this.pricingVersion}, only 'v3' is allowed.`);
    }
    return {
      part: this.part,
      isBudgetary: this.isBudgetary,
      hasBeenReviewed: this.hasBeenReviewed,
      feedback: this.feedback,
      optionalFeedback: this.optionalFeedback,
      feedbackChecklistVersion: this.feedbackChecklistVersion,
      feedbackChecklistPasses: this.feedbackChecklistPasses,
      feedbackChecklistReviewed: this.feedbackChecklistReviewed,
      feedbackResponses: this.feedbackResponses,
      orderedDfmSelections: this.orderedDfmSelections,
      needsReview: this.needsReview,
      pricingV2: this.pricingV2,
      pricingV3: this.pricingV3,
      pricingVersion: this.pricingVersion,
      status: this.status,
    };
  }

  /**
   * Creates a {@link Quote} instance from Firestore data.
   * @param {any} docData - Raw Firestore document.
   * @returns {Quote} A {@link Quote} instance.
   */
  static fromFirestore(docData) {
    return new Quote(docData);
  }
}
